import { createRoute, lazyRouteComponent } from '@tanstack/react-router';
import { protectedRoute, redirect } from './_root';

export const marketingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing',
  beforeLoad: () => {
    throw redirect({ to: '/marketing/actions' });
  },
});

type MarketingYearSearch = { year?: number };
/** R10 — unsaved draft values a drag & drop hands to the action sheet. */
type MarketingActionDetailSearch = {
  year?: number;
  highlight?: string;
  draftPlanMonth?: string;
  draftStartDate?: string;
  draftEndDate?: string;
  draftMarketId?: string;
  draftMemberId?: string;
  draftOwnerId?: string;
  draftStatusId?: string;
  draftTypeId?: string;
};

const DRAFT_DATE = /^\d{4}-\d{2}-\d{2}$/;
const DRAFT_ID_KEYS = [
  'draftMarketId',
  'draftMemberId',
  'draftOwnerId',
  'draftStatusId',
  'draftTypeId',
] as const;

const validateYearSearch = (
  input: Record<string, unknown>
): MarketingYearSearch => {
  const year = Number(input.year);
  return Number.isInteger(year) && year >= 2000 && year <= 2100 ? { year } : {};
};

const validateActionDetailSearch = (
  input: Record<string, unknown>
): MarketingActionDetailSearch => {
  const out: MarketingActionDetailSearch = validateYearSearch(input);
  if (typeof input.highlight === 'string' && input.highlight.length > 0) {
    out.highlight = input.highlight;
  }
  if (
    typeof input.draftPlanMonth === 'string' &&
    /^\d{4}-\d{2}$/.test(input.draftPlanMonth)
  ) {
    out.draftPlanMonth = input.draftPlanMonth;
  }
  for (const key of ['draftStartDate', 'draftEndDate'] as const) {
    const v = input[key];
    if (typeof v === 'string' && DRAFT_DATE.test(v)) out[key] = v;
  }
  for (const key of DRAFT_ID_KEYS) {
    const v = input[key];
    // An empty draftMarketId means "move to Unassigned".
    if (typeof v === 'string' && v.length <= 64) out[key] = v;
  }
  return out;
};

/** `new=1` opens the table with the New-action drawer (header button on the
 *  other views). */
const validateActionsSearch = (
  input: Record<string, unknown>
): MarketingYearSearch & { new?: 1 } => {
  const out: MarketingYearSearch & { new?: 1 } = validateYearSearch(input);
  if (Number(input.new) === 1) out.new = 1;
  return out;
};

export const marketingActionsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/actions',
  validateSearch: validateActionsSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/actions').then((m) => ({
      default: m.MarketingActionsPage,
    }))
  ),
});

export const marketingActionDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/actions/$code',
  validateSearch: validateActionDetailSearch,
  // The sheet is a drawer over the table: the detail route renders the table
  // page with the drawer open for `$code` (deep links keep working).
  component: lazyRouteComponent(() =>
    import('@marketing/pages/actions').then((m) => ({
      default: m.MarketingActionsPage,
    }))
  ),
});

export const marketingMapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/map',
  validateSearch: validateYearSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/map').then((m) => ({
      default: m.MarketingMapPage,
    }))
  ),
});

export const marketingTasksRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/tasks',
  validateSearch: validateYearSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/tasks').then((m) => ({
      default: m.MarketingTasksPage,
    }))
  ),
});

export const marketingDashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/dashboard',
  validateSearch: validateYearSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/dashboard').then((m) => ({
      default: m.MarketingDashboardPage,
    }))
  ),
});

export const marketingSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/settings',
  validateSearch: validateYearSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/settings').then((m) => ({
      default: m.MarketingSettingsPage,
    }))
  ),
});

export const marketingOverheadsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'marketing/overheads',
  validateSearch: validateYearSearch,
  component: lazyRouteComponent(() =>
    import('@marketing/pages/overheads').then((m) => ({
      default: m.MarketingOverheadsPage,
    }))
  ),
});
